import {
  Accordion,
  Box,
  Button,
  Columns,
  Container,
  Divider,
  Heading,
  Stack,
  Text,
  Visible,
} from '@spaceship-fspl/components';
import {
  boxShadow,
  color,
  display,
  fontSize,
  getColor,
  justifyContent,
  linearGradient,
  marginLeft,
  marginRight,
  marginTop,
  match,
  paddingLeft,
  paddingRight,
  rgba,
  transition,
} from '@spaceship-fspl/styles';
import { TrackingCategory } from '@spaceship-fspl/tracking';
import { PanelSection } from 'components';
import lowerDepositDesktopImage from 'images/first-home_lower-deposit-desktop.svg';
import lowerDepositMobileImage from 'images/first-home_lower-deposit-mobile.svg';
import actStateIconImage from 'images/first-home_states_act.svg';
import nswStateIconImage from 'images/first-home_states_nsw.svg';
import ntStateIconImage from 'images/first-home_states_nt.svg';
import qldStateIconImage from 'images/first-home_states_qld.svg';
import saStateIconImage from 'images/first-home_states_sa.svg';
import tasStateIconImage from 'images/first-home_states_tas.svg';
import vicStateIconImage from 'images/first-home_states_vic.svg';
import waStateIconImage from 'images/first-home_states_wa.svg';
import Link from 'next/link';
import React from 'react';
import styled from 'styled-components';

export interface LowerDepositPanelProps {
  id?: string | null;
  title?: string | null;
  tagline?: React.ReactNode;
  faqTitle?: string | null;
  faqDescription?: React.ReactNode;
  statesTitle?: string | null;
  states?: Array<{ state?: string | null }> | null;
}

export const LowerDepositPanel: React.FC<LowerDepositPanelProps> = ({
  id,
  title,
  tagline,
  faqTitle,
  faqDescription,
  statesTitle,
  states,
}) => {
  return (
    <TrackingCategory category="Lower Deposit Panel">
      <PanelSection id={id}>
        <Container>
          <Stack spaceY="xl">
            <Columns
              alignX="center"
              alignY={{ lg: 'center' }}
              spaceX={{ lg: 'xxl' }}
              spaceY={{ xs: 'lg', lg: 'none' }}
            >
              <Columns.Column width={{ lg: 1 / 2 }}>
                <Stack spaceY={{ xs: 'sm', lg: 'md' }}>
                  <Heading
                    component="h2"
                    variant={2}
                    color="neutral.100"
                    align={{ xs: 'center', lg: 'left' }}
                  >
                    {title}
                  </Heading>

                  <Text
                    variant={1}
                    color="neutral.080"
                    align={{ xs: 'center', lg: 'left' }}
                    component="div"
                  >
                    {tagline}
                  </Text>
                </Stack>
              </Columns.Column>

              <Columns.Column width={{ lg: 1 / 2 }}>
                <Box display="flex" justifyContent="center">
                  <Visible isHidden={{ lg: true }}>
                    <img src={lowerDepositMobileImage} alt={title ?? ''} />
                  </Visible>

                  <Visible isHidden={{ xs: true, lg: false }}>
                    <img src={lowerDepositDesktopImage} alt={title ?? ''} />
                  </Visible>
                </Box>
              </Columns.Column>
            </Columns>

            <div>
              <Divider color="neutral.050" />

              <Accordion title={faqTitle} variant="faq">
                <Box marginBottom="lg">
                  <Stack spaceY="lg">
                    <Text
                      variant={1}
                      color="neutral.080"
                      component="div"
                      isBold={false}
                    >
                      {faqDescription}
                    </Text>

                    {states && (
                      <Stack spaceY={{ xs: 'xxs', md: 'sm' }}>
                        <Heading
                          variant={5}
                          component="h5"
                          align={{ xs: 'center', md: 'left' }}
                        >
                          {statesTitle}
                        </Heading>

                        <StyledStatesContainer>
                          {states.map(({ state }) => {
                            let stateIcon;

                            switch (state?.toLowerCase()) {
                              case 'act':
                                stateIcon = actStateIconImage;
                                break;
                              case 'nsw':
                                stateIcon = nswStateIconImage;
                                break;
                              case 'nt':
                                stateIcon = ntStateIconImage;
                                break;
                              case 'qld':
                                stateIcon = qldStateIconImage;
                                break;
                              case 'sa':
                                stateIcon = saStateIconImage;
                                break;
                              case 'tas':
                                stateIcon = tasStateIconImage;
                                break;
                              case 'vic':
                                stateIcon = vicStateIconImage;
                                break;
                              case 'wa':
                                stateIcon = waStateIconImage;
                                break;
                            }

                            return (
                              <Link
                                href={`/first-home/${state?.toLowerCase()}`}
                                key={state}
                              >
                                <StyledStateButton
                                  variant="primary"
                                  size={{ xs: 'sm', md: 'lg' }}
                                >
                                  <Box display="flex" alignItems="center">
                                    <StyledImageContainer>
                                      <StyledStateIconImage src={stateIcon} />
                                    </StyledImageContainer>

                                    {state}
                                  </Box>
                                </StyledStateButton>
                              </Link>
                            );
                          })}
                        </StyledStatesContainer>
                      </Stack>
                    )}
                  </Stack>
                </Box>
              </Accordion>

              <Divider color="neutral.050" />
            </div>
          </Stack>
        </Container>
      </PanelSection>
    </TrackingCategory>
  );
};

const StyledStatesContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  ${justifyContent({ xs: 'center', md: 'flex-start' })}
`;

const StyledStateButton = styled(Button)`
  ${color('neutral.100')}
  ${marginLeft({ xs: 'xxs', md: 'none' })}
  ${marginRight({ xs: 'xxs', md: 'md' })}
  ${marginTop({ xs: 'sm', md: 'md' })}
  ${paddingLeft('sm')}
  ${paddingRight({ xs: 'sm', md: 'md' })}
  ${fontSize({ xs: '16px', md: '20px', lg: '32px' })}
  background: none;
  min-width: auto;
  position: relative;
  z-index: 1;

  :not(:disabled):hover {
    background: ${getColor('neutral.050')};
  }

  ${match('md')`
    font-weight: 600;
    border: 1px solid ${rgba('neutral.080', 1)};

    ::before {
      ${transition}
      content: '';
      background: white;
      border-radius: 32px;
      height: 100%;
      width: 100%;
      position: absolute;
      top: 0;
      left: 0;
      z-index: -1;
      opacity: 1;
    }

    &, :not(:disabled), :not(:disabled):hover {
      background: ${linearGradient({
        angle: '238.64deg',
        colorStops: [
          ['blue.050', '-90.67%'],
          ['indigo.050', '122.19%'],
        ],
      })};
    }

    :hover, :not(:disabled):hover {
      ${color('neutral.000')}
      ${boxShadow('md')}
      border: 1px solid ${rgba('neutral.080', 0)};

      ::before {
        opacity: 0;
      }
    }
  `}
`;

const StyledImageContainer = styled.div`
  ${display({ xs: 'none', md: 'block' })}
  ${marginRight('md')}
  margin-top: -16px;
`;

const StyledStateIconImage = styled.img`
  height: 74px;
  width: 74px;
`;
